import { v4 as uuid4 } from 'uuid';

type GaMfaInitProps = {
  token: string;
  clientId: string;
};

type MfaEnableOrVerifyProps = {
  code: string;
  token: string;
  clientId: string;
};

type revokeMfaWithPinProps = {
  pin: string;
  token: string;
};

type updateMfaPinProps = {
  pin: string;
  newPin: string;
  token: string;
};

type sendSmsMfaCodeProps = {
  smsMfaPhone?: string;
  token: string;
};

type updatePortalPasswordProps = {
  userid: string;
  ott: string;
  password: string;
};

type updateIdentityPortalPasswordProps = {
  userName: string;
  resetCode: string;
  newPassword: string;
  clientId: string;
};

type changePasswordProps = {
  token: string;
  oldPassword: string;
  newPassword: string;
};

export async function checkSAML(username: string): Promise<string | null> {
  if (!username.includes('@')) return null;
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/check/${username}`
    );
    const json = await response.json();
    return json.provider || null;
  } catch (ex) {
    return null;
  }
}

export async function verifyNFetchInvite({
  invite,
  email,
  clientId,
}: {
  invite: string;
  email: string;
  clientId: string;
}): Promise<string | null> {
  if (!email.includes('@')) return null;
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/invite/${email}/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ invite }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          'app-id': clientId,
        },
      }
    );
    const respJson = await response.json();
    if (respJson.result === 'error') throw new Error(respJson.message);
    return respJson.provider || null;
  } catch (ex) {
    throw ex;
  }
}

export async function generateResignedTokens({
  token,
  refreshToken,
  accessToken,
  mfaToken,
  clientId,
}: {
  token: string;
  refreshToken?: string;
  accessToken?: string;
  mfaToken: string;
  clientId: string;
}): Promise<{ access: string; id: string }> {
  const response = await fetch(`${process.env.authBackend}/authv2/token`, {
    method: 'POST',
    body: JSON.stringify({
      ...(refreshToken ? { refresh: refreshToken } : { access: accessToken }),
      mfa: mfaToken,
    }),
    headers: {
      'x-rkn-correlation-id': uuid4(),
      authorizationtoken: token,
      'app-id': clientId,
    },
  });
  return response.json();
}

export async function updatePortalPassword({
  userid,
  ott,
  password,
}: updatePortalPasswordProps): Promise<void> {
  try {
    await fetch(`${process.env.authBackend}/authv2/password/reset`, {
      method: 'PUT',
      body: JSON.stringify({ userid, token: ott, password }),
    });
  } catch (ex) {}
}

export async function updateIdentityPortalPassword({
  userName,
  resetCode,
  newPassword,
  clientId,
}: updateIdentityPortalPasswordProps): Promise<void> {
  try {
    await fetch(`${process.env.authBackend}/authv2/password/reset`, {
      method: 'POST',
      body: JSON.stringify({ userName, resetCode, newPassword, clientId }),
    });
  } catch (ex) {}
}

export async function changePassword({
  token,
  oldPassword,
  newPassword,
}: changePasswordProps): Promise<void> {
  await fetch(`${process.env.authBackend}/authv2/password/change`, {
    method: 'POST',
    headers: {
      'x-rkn-correlation-id': uuid4(),
      authorizationtoken: token,
    },
    body: JSON.stringify({ oldPassword, newPassword }),
  });
}

// SMS && GA
export async function validateMfaToken(
  mfaToken: string,
  token: string,
  clientId: string
): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/mfa/tokenvalidate?mfaToken=${mfaToken}`,
      {
        method: 'GET',
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
          'app-id': clientId,
        },
      }
    );
    return response.json();
  } catch (ex) {
    if (ex instanceof Error && ex.message === 'Token not verified') {
      return;
    }
    throw ex;
  }
}

export async function GAMFAInit({
  token,
  clientId,
}: GaMfaInitProps): Promise<any | undefined> {
  try {
    const response = await fetch(`${process.env.authBackend}/authv2/mfa`, {
      method: 'POST',
      headers: {
        'x-rkn-correlation-id': uuid4(),
        authorizationtoken: token,
        'app-id': clientId,
      },
    });
    const result: any = await response.json();
    if (response.status < 300) {
      return { qrCodeUrl: result.qrCodeImg, qrCode: result.qrCode };
    } else if (400 <= response.status && response.status < 500) {
      throw new Error(result.message);
    } else if (500 <= response.status) {
      throw new Error('mfa service failed');
    }
  } catch (ex) {
    throw ex;
  }
}

// GA
export async function GAMFAVerify({
  code,
  token,
  clientId,
}: MfaEnableOrVerifyProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/mfa/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ verifyCode: code }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
          'app-id': clientId,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function GAMFAEnable({
  code,
  token,
  clientId,
}: MfaEnableOrVerifyProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/mfa/enable`,
      {
        method: 'POST',
        body: JSON.stringify({ verifyCode: code }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
          'app-id': clientId,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS MFA
export async function SmsMfaEnable({
  code,
  token,
  clientId,
}: MfaEnableOrVerifyProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/sms/mfa/enable`,
      {
        method: 'POST',
        body: JSON.stringify({ smsMfaCode: code }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
          'app-id': clientId,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS MFA
export async function verifyMFACode({
  code,
  token,
  clientId,
}: MfaEnableOrVerifyProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/sms/mfa/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ smsMfaCode: code }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
          'app-id': clientId,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function revokeGAMFAWithCode(verifyCode: string, token: string) {
  try {
    const response = await fetch(`${process.env.authBackend}/authv2/mfa`, {
      method: 'DELETE',
      body: JSON.stringify({ verifyCode }),
      headers: {
        'x-rkn-correlation-id': uuid4(),
        authorizationtoken: token,
      },
    });
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS MFA
export async function revokeSMSMFAWithCode(code: string, token: string) {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/sms/mfa/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ smsMfaCode: code, revokeMfa: true }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS & GA ??
export async function revokeMfaWithPin({ pin, token }: revokeMfaWithPinProps) {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/mfa/recovery`,
      {
        method: 'DELETE',
        body: JSON.stringify({ pin }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(
        typeof resp.message === 'object'
          ? `${resp.message.error} Remaining ${resp.message.remainingAttempts} attempts.`
          : resp.message
      );
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS & GA??
export async function updateMFAPin({
  pin,
  newPin,
  token,
}: updateMfaPinProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/mfa/recovery`,
      {
        method: 'PUT',
        body: JSON.stringify({ pin, newPin }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

// SMS
export async function sendMFACode({
  smsMfaPhone,
  token,
}: sendSmsMfaCodeProps): Promise<any | undefined> {
  try {
    const response = await fetch(
      `${process.env.authBackend}/authv2/sms/mfa/send`,
      {
        method: 'POST',
        body: JSON.stringify({ ...(smsMfaPhone ? { smsMfaPhone } : {}) }),
        headers: {
          'x-rkn-correlation-id': uuid4(),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          authorizationtoken: token,
        },
      }
    );
    const resp: any = await response.json();
    if (resp.result === 'success') {
      return resp;
    } else {
      throw new Error(resp.message);
    }
  } catch (ex) {
    throw ex;
  }
}

export async function checkExistingEmail(
  email: string,
  validity: string,
  clientId: string
) {
  if (validity === 'invalid') {
    return Promise.reject();
  }
  const resp = await fetch(
    `${process.env.authBackend}/authv2/email/${encodeURIComponent(email)}`,
    {
      method: 'GET',
      headers: {
        'x-rkn-correlation-id': uuid4(),
        'app-id': clientId,
      },
    }
  );
  if (!resp.ok) {
    throw new Error('Email check failed');
  }
  return resp.json();
}
